import type {
  PlanogramSubtask,
  SubtaskStatus,
  StatusChange,
  SubtaskComment,
  SubtaskAttachment,
  SubtaskFilters,
  PaginationParams,
  ApiResponse
} from '../types/plan-sub-task';

import type {
  PaginatedResponse
} from '../types/index';

import {
  MOCK_SUBTASKS,
  MOCK_COMMENTS,
  MOCK_ATTACHMENTS,
  MOCK_MILESTONES,
  MOCK_ASSIGNEES,
  getSubtaskByCode,
  getCommentsBySubtaskCode,
  getAttachmentsBySubtaskCode,
  addCommentToSubtask,
  addAttachmentToSubtask,
  removeAttachmentFromSubtask
} from '../mock/subtaskData';

import {
  validateStatusTransition,
  validateSubtaskCompletion,
  validateComment,
  validateFileUploads,
} from '../utils/planSubTaskValidation';


// 导入 API 端点常量
import { API_ENDPOINTS } from '../utils/apiEndpoints';
// 导入响应转换工具
import { createSuccessResponse, createErrorResponse, createPaginatedResponse } from '../utils/responseTransformer';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';  

// Simulate API delay
const delay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// Generate trace ID for API responses
const generateTraceId = () => Math.random().toString(36).substr(2, 15) + Math.random().toString(36).substr(2, 15);

// In-memory storage for subtasks (simulating database)
let subtasksStorage = [...MOCK_SUBTASKS];

/**
 * Service class for Planogram Subtask operations
 */
export class PlanogramSubtaskService {
  private static instance: PlanogramSubtaskService;
  private static useMock: boolean = true;

  /**
   * Get singleton instance
   */
  public static getInstance(): PlanogramSubtaskService {
    if (!PlanogramSubtaskService.instance) {
      PlanogramSubtaskService.instance = new PlanogramSubtaskService();
    }
    return PlanogramSubtaskService.instance;
  }

  /**
   * Set whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    PlanogramSubtaskService.useMock = useMock;
  }

  /**
   * Get whether using mock data
   */
  public static getUseMock(): boolean {
    return PlanogramSubtaskService.useMock;
  }

  /**
   * Get paginated subtasks with filtering and sorting
   */
  public async getSubtasks(
    filters: SubtaskFilters = {},
    pagination: PaginationParams = { current: 1, pageSize: 10 }
  ): Promise<ApiResponse<PaginatedResponse<PlanogramSubtask>>> {
    try {
      // 根据配置决定使用 mock 数据还是调用真实 API
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay();
        
        let filteredSubtasks = [...subtasksStorage];

        // Apply filters
        if (filters.planCode) {
          filteredSubtasks = filteredSubtasks.filter(subtask => 
            subtask.planCode === filters.planCode
          );
        }

        if (filters.status && filters.status !== 'all') {
          filteredSubtasks = filteredSubtasks.filter(subtask => 
            subtask.status === filters.status
          );
        }

        if (filters.milestone) {
          filteredSubtasks = filteredSubtasks.filter(subtask =>
            subtask.milestoneName === filters.milestone
          );
        }

        if (filters.assignee) {
          filteredSubtasks = filteredSubtasks.filter(subtask =>
            subtask.assignee === filters.assignee
          );
        }

        if (filters.dueDateStart && filters.dueDateEnd) {
          filteredSubtasks = filteredSubtasks.filter(subtask => {
            const dueDateTimestamp = new Date(subtask.dueDate).getTime();
            return dueDateTimestamp >= filters.dueDateStart! && dueDateTimestamp <= filters.dueDateEnd!;
          });
        } else if (filters.dueDateStart) {
          filteredSubtasks = filteredSubtasks.filter(subtask => {
            const dueDateTimestamp = new Date(subtask.dueDate).getTime();
            return dueDateTimestamp >= filters.dueDateStart!;
          });
        } else if (filters.dueDateEnd) {
          filteredSubtasks = filteredSubtasks.filter(subtask => {
            const dueDateTimestamp = new Date(subtask.dueDate).getTime();
            return dueDateTimestamp <= filters.dueDateEnd!;
          });
        }

        if (filters.overdueFlag !== undefined && filters.overdueFlag !== 'all') {
          filteredSubtasks = filteredSubtasks.filter(subtask =>
            subtask.overdueFlag === filters.overdueFlag
          );
        }

        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          filteredSubtasks = filteredSubtasks.filter(subtask =>
            subtask.subtaskName.toLowerCase().includes(searchTerm) ||
            subtask.subtaskCode.toLowerCase().includes(searchTerm) ||
            subtask.milestoneName.toLowerCase().includes(searchTerm)
          );
        }

        // Apply sorting (default: by due date ascending)
        filteredSubtasks.sort((a, b) => {
          // First sort by status priority (Active > Ongoing > Pending > Finished)
          const statusPriority = { 'Active': 4, 'Ongoing': 3, 'Pending': 2, 'Finished': 1 };
          const aPriority = statusPriority[a.status];
          const bPriority = statusPriority[b.status];
          
          if (aPriority !== bPriority) {
            return bPriority - aPriority;
          }
          
          // Then sort by due date
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        });

        // Apply pagination
        const total = filteredSubtasks.length;
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const paginatedData = filteredSubtasks.slice(startIndex, endIndex);

        // 使用统一的PaginatedResponse格式
        const paginatedResponse: PaginatedResponse<PlanogramSubtask> = {
          tableData: paginatedData,
          paging: {
            currentPage: pagination.current,
            totalCount: total,
            pageSize: pagination.pageSize
          }
        };

        return createSuccessResponse(paginatedResponse);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.PAGE, { 
          ...filters, ...pagination, currentPage: pagination.current,
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching subtasks:', error);
      return createErrorResponse<PaginatedResponse<PlanogramSubtask>>(
        '获取子任务列表失败',
        'SUBTASK_LIST_ERROR',
      );
    }
  }

  /**
   * Get paginated subtasks with filtering and sorting
   */
  public async getSubtasks4Me(
    filters: SubtaskFilters = {},
    pagination: PaginationParams = { current: 1, pageSize: 10 }
  ): Promise<ApiResponse<PaginatedResponse<PlanogramSubtask>>> {
    // 根据配置决定使用 mock 数据还是调用真实 API
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay();
        
        let filteredSubtasks = [...subtasksStorage];

        // Apply filters
        if (filters.status && filters.status !== 'all') {
          filteredSubtasks = filteredSubtasks.filter(subtask => 
            subtask.status === filters.status
          );
        }

        if (filters.milestone) {
          filteredSubtasks = filteredSubtasks.filter(subtask =>
            subtask.milestoneName === filters.milestone
          );
        }

        if (filters.assignee) {
          filteredSubtasks = filteredSubtasks.filter(subtask =>
            subtask.assignee === filters.assignee
          );
        }

        if (filters.dueDateStart && filters.dueDateEnd) {
          filteredSubtasks = filteredSubtasks.filter(subtask => {
            const dueDateTimestamp = new Date(subtask.dueDate).getTime();
            return dueDateTimestamp >= filters.dueDateStart! && dueDateTimestamp <= filters.dueDateEnd!;
          });
        } else if (filters.dueDateStart) {
          filteredSubtasks = filteredSubtasks.filter(subtask => {
            const dueDateTimestamp = new Date(subtask.dueDate).getTime();
            return dueDateTimestamp >= filters.dueDateStart!;
          });
        } else if (filters.dueDateEnd) {
          filteredSubtasks = filteredSubtasks.filter(subtask => {
            const dueDateTimestamp = new Date(subtask.dueDate).getTime();
            return dueDateTimestamp <= filters.dueDateEnd!;
          });
        }

        if (filters.overdueFlag !== undefined && filters.overdueFlag !== 'all') {
          filteredSubtasks = filteredSubtasks.filter(subtask =>
            subtask.overdueFlag === filters.overdueFlag
          );
        }

        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          filteredSubtasks = filteredSubtasks.filter(subtask =>
            subtask.subtaskName.toLowerCase().includes(searchTerm) ||
            subtask.subtaskCode.toLowerCase().includes(searchTerm) ||
            subtask.milestoneName.toLowerCase().includes(searchTerm)
          );
        }

        // Apply sorting (default: by due date ascending)
        filteredSubtasks.sort((a, b) => {
          // First sort by status priority (Active > Ongoing > Pending > Finished)
          const statusPriority = { 'Active': 4, 'Ongoing': 3, 'Pending': 2, 'Finished': 1 };
          const aPriority = statusPriority[a.status];
          const bPriority = statusPriority[b.status];
          
          if (aPriority !== bPriority) {
            return bPriority - aPriority;
          }
          
          // Then sort by due date
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        });

        // Apply pagination
        const total = filteredSubtasks.length;
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        const paginatedData = filteredSubtasks.slice(startIndex, endIndex);

        // 使用统一的PaginatedResponse格式
        const paginatedResponse: PaginatedResponse<PlanogramSubtask> = {
          tableData: paginatedData,
          paging: {
            currentPage: pagination.current,
            totalCount: total,
            pageSize: pagination.pageSize
          }
        };

        return createSuccessResponse(paginatedResponse);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.PAGE_4_MY, { 
          ...filters, ...pagination , currentPage: pagination.current,
        });
        return response.data;
      }
  }

  /**
   * Get subtask by code
   */
  public async getSubtaskByCode(code: string): Promise<ApiResponse<PlanogramSubtask>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay();

        const subtask = subtasksStorage.find(s => s.subtaskCode === code);

        if (!subtask) {
          return createErrorResponse('子任务不存在', 'SUBTASK_NOT_FOUND');
        }

        return createSuccessResponse(subtask);
      } else {
        // 调用真实 API
        const response = await httpClient.get(API_ENDPOINTS.PLAN_SUBTASK.QUERY_BY_CODE + `?code=${code}`);
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching subtask details:', error);
      return createErrorResponse('获取子任务详情失败', 'SUBTASK_DETAIL_ERROR');
    }
  }

  /**
   * Create a new subtask
   */
  public async createSubtask(subtaskData: any): Promise<ApiResponse<PlanogramSubtask>> {
    if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(500);
        
        // Generate a new subtask with mock data
        const newSubtask: PlanogramSubtask = {
          id: Date.now(),
          subtaskCode: `ST${Math.floor(100000 + Math.random() * 900000)}`,
          subtaskName: subtaskData.taskName,
          milestoneName: subtaskData.milestoneName,
          assignee: subtaskData.assignee,
          status: 'Pending',
          gmtCreate: new Date().toISOString(),
          creatorName: 'current_user',
          milestoneStartDate: subtaskData.plannedCompletionTime,
          dueDate: subtaskData.plannedCompletionTime,
          statusChangeHistory: [],
          autoStatusUpdate: false,
          overdueFlag: false,
          planEndDate: subtaskData.plannedCompletionTime,
          planCode: subtaskData.planCode,
          planName: subtaskData.planName
        };
        
        return createSuccessResponse(newSubtask);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.CREATE, subtaskData);
        return response.data;
      }
  }

  /**
   * Update an existing subtask
   */
  public async updateSubtask(subtaskData: any): Promise<ApiResponse<PlanogramSubtask>> {
    if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(500);
        
        // Return updated subtask mock data
        const updatedSubtask: PlanogramSubtask = {
          id: subtaskData.id,
          subtaskCode: subtaskData.subtaskCode,
          subtaskName: subtaskData.taskName,
          milestoneName: subtaskData.milestoneName,
          assignee: subtaskData.assignee,
          status: subtaskData.status || 'Pending',
          gmtCreate: subtaskData.gmtCreate || new Date().toISOString(),
          creatorName: subtaskData.creatorName || 'current_user',
          milestoneStartDate: subtaskData.plannedCompletionTime,
          startDate: subtaskData.startDate,
          dueDate: subtaskData.plannedCompletionTime,
          completionDate: subtaskData.completionDate,
          deliverables: subtaskData.deliverables,
          remarks: subtaskData.description,
          statusChangeHistory: subtaskData.statusChangeHistory || [],
          autoStatusUpdate: subtaskData.autoStatusUpdate || false,
          overdueFlag: subtaskData.overdueFlag || false,
          planEndDate: subtaskData.plannedCompletionTime,
          finalStatusDate: subtaskData.finalStatusDate,
          planCode: subtaskData.planCode,
          planName: subtaskData.planName
        };
        
        return createSuccessResponse(updatedSubtask);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.UPDATE, subtaskData);
        return response.data;
      }
  }

  public async deleteSubtask(id: any): Promise<ApiResponse<any>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        // 在模拟模式下，从存储中移除子任务
        return createSuccessResponse(null);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.DELETE, {id});
        return response.data;
      }
    } catch (error) {
      console.error('Error deleting subtask:', error);
      return createErrorResponse<any>(
        '删除子任务失败',
        'SUBTASK_DELETE_ERROR',
      );
    }
  }

  public async cancelSubtask(id: any): Promise<ApiResponse<any>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        // 在模拟模式下，从存储中移除子任务
        return createSuccessResponse(null);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.CANCEL, {id});
        return response.data;
      }
    } catch (error) {
      console.error('Error deleting subtask:', error);
      return createErrorResponse<any>(
        '删除子任务失败',
        'SUBTASK_DELETE_ERROR',
      );
    }
  }

  /**
   * Update subtask status
   */
  public async updateSubtaskStatus(
    code: string,
    newStatus: SubtaskStatus,
    notes?: string,
    execution_notes?: string
  ): Promise<ApiResponse<PlanogramSubtask>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(600);

        const subtaskIndex = subtasksStorage.findIndex(s => s.subtaskCode === code);

        if (subtaskIndex === -1) {
          return createErrorResponse('子任务不存在', 'SUBTASK_NOT_FOUND');
        }

        const subtask = subtasksStorage[subtaskIndex];
        const previousStatus = subtask.status;

        // Validate status transition
        const validation = validateStatusTransition(previousStatus, newStatus);
        if (!validation.isValid) {
          return createErrorResponse(validation.errors[0]?.message || '状态转换无效', 'INVALID_STATUS_TRANSITION');
        }

        // Create status change record
        const statusChange: StatusChange = {
          id: generateId(),
          fromStatus: previousStatus,
          toStatus: newStatus,
          changeTime: new Date().toISOString(),
          changedBy: 'current_user',
          reason: notes || '用户手动更新状态',
          notes: notes
        };

        // Update subtask
        const updatedSubtask = {
          ...subtask,
          status: newStatus,
          statusChangeHistory: [...subtask.statusChangeHistory, statusChange]
        };

        // Add completion specific fields
        if (newStatus === 'Finished') {
          updatedSubtask.completionDate = new Date().toISOString();
          updatedSubtask.finalStatusDate = new Date().toISOString();
          if (execution_notes) {
            updatedSubtask.remarks = execution_notes;
          }
        }

        // Add start date for Ongoing status
        if (newStatus === 'Ongoing' && !subtask.startDate) {
          updatedSubtask.startDate = new Date().toISOString();
        }

        subtasksStorage[subtaskIndex] = updatedSubtask;

        return createSuccessResponse(updatedSubtask);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.SUBTASK_STATUS, {
          code,
          status: newStatus,
          notes,
          remarks: execution_notes
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error updating subtask status:', error);
      return createErrorResponse('状态更新失败', 'STATUS_UPDATE_ERROR');
    }
  }

  /**
   * Start subtask (change status to Ongoing)
   */
  public async startSubtask(id:number, code: string): Promise<ApiResponse<PlanogramSubtask>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        return this.updateSubtaskStatus(code, 'Ongoing', '用户开始执行任务');
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.SUBTASK_START, {
          id
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error starting subtask:', error);
      return createErrorResponse('开始任务失败', 'START_ERROR');
    }
  }

  /**
   * Batch update subtasks status by plan code
   * Used when plan is paused or activated to update related subtasks
   */
  public async batchUpdateSubtasksByPlan(
    planCode: string,
    newStatus: SubtaskStatus,
    reason?: string
  ): Promise<ApiResponse<PlanogramSubtask[]>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(800);

        // Find all subtasks related to the plan that are not in final states
        const relatedSubtasks = subtasksStorage.filter(subtask => 
          subtask.planCode === planCode && 
          !['Finished', 'Cancelled'].includes(subtask.status)
        );

        if (relatedSubtasks.length === 0) {
          return createSuccessResponse([]);
        }

        const updatedSubtasks: PlanogramSubtask[] = [];
        const currentTime = new Date().toISOString();

        // Update each related subtask
        for (const subtask of relatedSubtasks) {
          const subtaskIndex = subtasksStorage.findIndex(s => s.subtaskCode === subtask.subtaskCode);
          
          if (subtaskIndex !== -1) {
            // Create status change record
            const statusChange: StatusChange = {
              id: generateId(),
              fromStatus: subtask.status,
              toStatus: newStatus,
              changeTime: currentTime,
              changedBy: 'system',
              reason: reason || `计划状态变更导致的自动更新`,
              notes: `由于陈列计划${planCode}状态变更，子任务状态自动更新为${newStatus}`
            };

            // Update subtask
            const updatedSubtask = {
              ...subtask,
              status: newStatus,
              statusChangeHistory: [...subtask.statusChangeHistory, statusChange]
            };

            subtasksStorage[subtaskIndex] = updatedSubtask;
            updatedSubtasks.push(updatedSubtask);
          }
        }

        return createSuccessResponse(updatedSubtasks);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.BATCH_UPDATE_BY_PLAN, {
          planCode,
          status: newStatus,
          reason
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error batch updating subtasks:', error);
      return createErrorResponse('批量更新子任务状态失败', 'BATCH_UPDATE_ERROR');
    }
  }

  /**
   * Complete subtask (change status to Finished)
   */
  public async completeSubtask(
    id:number,
    code: string,
    remarks: string,
    files?: File[]
  ): Promise<ApiResponse<PlanogramSubtask>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(800);

        // Validate completion
        const completionValidation = validateSubtaskCompletion(remarks, []);
        if (!completionValidation.isValid) {
          return createErrorResponse(completionValidation.errors[0]?.message || '完成验证失败', 'INVALID_COMPLETION');
        }

        // Validate files if provided
        if (files && files.length > 0) {
          const fileValidation = validateFileUploads(files);
          if (!fileValidation.isValid) {
            return createErrorResponse(fileValidation.errors[0]?.message || '文件验证失败', 'INVALID_FILES');
          }
        }

        const subtaskIndex = subtasksStorage.findIndex(s => s.subtaskCode === code);

        if (subtaskIndex === -1) {
          return createErrorResponse('子任务不存在', 'SUBTASK_NOT_FOUND');
        }

        const result = await this.updateSubtaskStatus(
          code,
          'Finished',
          '用户完成任务',
          remarks
        );

        // Handle file uploads if provided
        if (files && files.length > 0 && result.success) {
          for (const file of files) {
            await this.uploadAttachment(code, file);
          }
        }

        return result;
      } else {
        // 调用真实 API
        
        // 使用 FormData 处理文件上传
        const formData = new FormData();
        formData.append('remarks', remarks);
        
        if (files && files.length > 0) {
          files.forEach((file, index) => {
            formData.append(`files[${index}]`, file);
          });
        }

        let req = {
          id,
          code,
          remarks,
          fileUrls: []
        }
        
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.SUBTASK_COMPLETE, req);
        return response.data;
      }
    } catch (error) {
      console.error('Error completing subtask:', error);
      return createErrorResponse('任务完成失败', 'COMPLETION_ERROR');
    }
  }

  /**
   * Add comment to subtask
   */
  public async addComment(
    subtaskCode: string,
    content: string,
    author: string = 'current_user'
  ): Promise<ApiResponse<SubtaskComment>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(300);

        // Validate comment
        const validation = validateComment(content);
        if (!validation.isValid) {
          return createErrorResponse(validation.errors[0]?.message || '评论验证失败', 'INVALID_COMMENT');
        }

        const subtask = subtasksStorage.find(s => s.subtaskCode === subtaskCode);
        if (!subtask) {
          return createErrorResponse('子任务不存在', 'SUBTASK_NOT_FOUND');
        }

        const comment: SubtaskComment = {
          id: generateId(),
          subtaskCode: subtaskCode,
          content,
          author,
          gmtCreate: new Date().toISOString()
        };

        addCommentToSubtask(subtaskCode, comment);

        return createSuccessResponse(comment);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.COMMENT_ADD, {
          subtaskCode,
          content,
          author
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      return createErrorResponse('添加评论失败', 'COMMENT_ERROR');
    }
  }

  /**
   * Get comments for subtask
   */
  public async getComments(subtaskCode: string): Promise<ApiResponse<SubtaskComment[]>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(200);

        const comments = getCommentsBySubtaskCode(subtaskCode);
        return createSuccessResponse(comments);
      } else {
        // 调用真实 API
        const response = await httpClient.get(API_ENDPOINTS.PLAN_SUBTASK.COMMENTS + '?subtaskCode=' + subtaskCode);
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      return createErrorResponse('获取评论失败', 'COMMENTS_ERROR');
    }
  }

  /**
   * Upload attachment to subtask
   */
  public async uploadAttachment(
    subtaskCode: string,
    file: File
  ): Promise<ApiResponse<SubtaskAttachment>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(1000);

        // Validate file
        const validation = validateFileUploads([file]);
        if (!validation.isValid) {
          return createErrorResponse(validation.errors[0]?.message || '文件验证失败', 'INVALID_FILE');
        }

        const subtask = subtasksStorage.find(s => s.subtaskCode === subtaskCode);
        if (!subtask) {
          return createErrorResponse('子任务不存在', 'SUBTASK_NOT_FOUND');
        }

        const attachment: SubtaskAttachment = {
          id: generateId(),
          subtaskCode: subtaskCode,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadTime: new Date().toISOString(),
          uploader: 'current_user',
          downloadUrl: `/api/files/download/${generateId()}-${file.name}`
        };

        addAttachmentToSubtask(subtaskCode, attachment);

        return createSuccessResponse(attachment);
      } else {
        
        let req = {
          code: subtaskCode,
          fileUrls: []
        }
        
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.ATTACHMENT_UPLOAD, req);
        return response.data;
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
      return createErrorResponse('文件上传失败', 'UPLOAD_ERROR');
    }
  }

  /**
   * Get attachments for subtask
   */
  public async getAttachments(subtaskCode: string): Promise<ApiResponse<SubtaskAttachment[]>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(200);

        const attachments = getAttachmentsBySubtaskCode(subtaskCode);
        return createSuccessResponse(attachments);
      } else {
        // 调用真实 API
        const response = await httpClient.get(API_ENDPOINTS.PLAN_SUBTASK.ATTACHMENTS + '?subtaskCode=' + subtaskCode);
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
      return createErrorResponse('获取附件失败', 'ATTACHMENTS_ERROR');
    }
  }

  /**
   * Delete attachment
   */
  public async deleteAttachment(attachmentId: string): Promise<ApiResponse<boolean>> {
    try {
      if (PlanogramSubtaskService.useMock && WHOLE_USE_MOCK) {
        await delay(300);

        const success = removeAttachmentFromSubtask(attachmentId);
        
        if (!success) {
          return createErrorResponse('附件不存在', 'ATTACHMENT_NOT_FOUND');
        }

        return createSuccessResponse(true);
      } else {
        // 调用真实 API
        const response = await httpClient.post(API_ENDPOINTS.PLAN_SUBTASK.ATTACHMENT_DELETE, {
          id: attachmentId
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      return createErrorResponse('删除附件失败', 'DELETE_ERROR');
    }
  }


  /**
   * Get milestones
   */
  public async getMilestones(): Promise<ApiResponse<string[]>> {
    try {
     return createSuccessResponse(MOCK_MILESTONES);
    } catch (error) {
      console.error('Error fetching milestones:', error);
      return createErrorResponse('获取里程碑失败', 'MILESTONES_ERROR');
    }
  }

  /**
   * Get assignees
   */
  public async getAssignees(): Promise<ApiResponse<string[]>> {
    try {
      return createSuccessResponse(MOCK_ASSIGNEES);
    } catch (error) {
      console.error('Error fetching assignees:', error);
      return createErrorResponse('获取负责人失败', 'ASSIGNEES_ERROR');
    }
  }
}

// Export singleton instance
export default PlanogramSubtaskService.getInstance();